import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setToken, getToken, getAccounts, upsertAccount, removeAccount } from "../lib/api";
import { translate } from "../lib/i18n";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const VAR_MAP = {
  bg_app: "--bg-app",
  bg_sidebar: "--bg-sidebar",
  bg_card: "--bg-card",
  text_primary: "--text-primary",
  text_secondary: "--text-secondary",
  brand_primary: "--brand-primary",
  brand_accent: "--brand-accent",
  border_color: "--border-color",
};

export function applyTheme(theme) {
  if (!theme) return;
  const r = document.documentElement;
  Object.entries(VAR_MAP).forEach(([k, cssVar]) => {
    if (theme[k]) r.style.setProperty(cssVar, theme[k]);
  });
}

export function applyFontScale(scale) {
  const v = Math.min(1.2, Math.max(0.85, Number(scale) || 1));
  document.documentElement.style.setProperty("--font-scale", String(v));
  localStorage.setItem("rannea_font_scale", String(v));
}

// Dernière échelle connue de l'appareil, appliquée avant /auth/me (pas de flash)
const bootScale = Number(localStorage.getItem("rannea_font_scale"));
if (bootScale) document.documentElement.style.setProperty("--font-scale", String(bootScale));

export function AuthProvider({ children }) {
  const [state, setState] = useState({ status: "loading", user: null, group: null, tspace: null, identity: null, memberships: [] });
  const [accounts, setAccountsState] = useState(getAccounts);
  const [lang, setLangState] = useState(() => localStorage.getItem("rannea_lang") || "fr");
  const t = useCallback((key, vars) => translate(lang, key, vars), [lang]);
  const applyLang = useCallback((l, persist) => {
    if (!l) return;
    setLangState(l);
    localStorage.setItem("rannea_lang", l);
    if (persist) api.put("/display/prefs", { lang: l }).catch(() => {});
  }, []);
  const setLang = (l) => applyLang(l, true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      if (!data.user && data.identity) {
        // Session « identité » : compte global vérifié, aucun groupe encore ouvert
        // → status "anon" pour que le routeur renvoie vers /connexion, où la page
        // affichera le sélecteur « Mes groupes » / la création de groupe.
        setState({ status: "anon", user: null, group: null, tspace: null, identity: data.identity, memberships: data.memberships || [] });
        return;
      }
      applyTheme(data.group?.theme);
      if (data.user?.display_prefs?.font_scale) applyFontScale(data.user.display_prefs.font_scale);
      applyLang(data.user?.display_prefs?.lang || data.group?.language);
      setState({ status: "ok", user: data.user, group: data.group, tspace: data.teaching_space || null, identity: data.identity || null, memberships: data.memberships || [] });
    } catch {
      setState({ status: "anon", user: null, group: null, tspace: null, identity: null, memberships: [] });
    }
  }, [applyLang]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setAuth = (data) => {
    applyTheme(data.group?.theme);
    if (data.user?.display_prefs?.font_scale) applyFontScale(data.user.display_prefs.font_scale);
    applyLang(data.user?.display_prefs?.lang || data.group?.language);
    if (data.token) {
      setToken(data.token);
      const spaceName = data.group?.name || data.teaching_space?.name;
      if (data.user && (spaceName || data.user.is_superadmin)) {
        setAccountsState(upsertAccount({
          token: data.token,
          login_id: data.user.login_id,
          prenom: data.user.prenom,
          group_id: data.group?.id || data.teaching_space?.id || null,
          group_name: spaceName || "Console Superadmin",
        }));
      }
    }
    setState({ status: "ok", user: data.user, group: data.group, tspace: data.teaching_space || null, identity: data.identity || null, memberships: data.memberships || [] });
  };

  // Bascule instantanée vers un autre groupe connecté (cookie synchronisé côté serveur)
  const switchAccount = async (acc) => {
    setToken(acc.token);
    try { await api.post("/auth/switch", { token: acc.token }); } catch {}
    window.location.assign("/tableau-de-bord");
  };

  // Bascule native vers un autre groupe de la même identité : nouvelle session membership
  // émise côté serveur puis rechargement complet (isolation totale du contexte tenant).
  const switchGroup = async (membershipId) => {
    try {
      const { data } = await api.post("/auth/switch-group", { membership_id: membershipId });
      if (data.token) setToken(data.token);
    } catch { return; }
    window.location.assign("/tableau-de-bord");
  };

  const logout = async () => {
    const remaining = removeAccount(getToken());
    try {
      await api.post("/auth/logout");
    } catch {}
    setToken(null);
    if (remaining.length) {
      await switchAccount(remaining[0]);
      return;
    }
    setAccountsState(remaining);
    setState({ status: "anon", user: null, group: null, tspace: null, identity: null, memberships: [] });
  };

  return (
    <AuthContext.Provider value={{ ...state, accounts, lang, t, setLang, refresh, setAuth, switchAccount, switchGroup, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
